import { useEffect, useRef, useState } from "react"
import { gsap } from "gsap"
import { PageComponent, Text } from "../../components"
import './style.css'
import { API_BASE_URL, SHOW_POINT } from '../../config'
import { StoreData, trackLinkClick } from './components/link-row'
import { StoreStyle } from './components/store-style'
import { Rating } from './components/rating'
import { SupportModal } from '../get-qr-admin/components/support-modal'

const API_URL = `${API_BASE_URL}/get-links`

const getStoreId = (): string => {
    const segments = window.location.pathname.split('/').filter(Boolean)
    return segments[segments.length - 1] ?? ''
}

const POINTS_PER_LINK = 10
const POINTS_PER_RATING = 10
const POINTS_PER_CARD_VISIT = 10

// Баллы за сессию на странице: после перезагрузки сбрасываются,
// повторное начисление за то же действие отклоняет бэкенд

// Ставит страница cards перед редиректом — значение равно пути, куда она ведёт
const FROM_CARD_KEY = 'qr_from_card'

// Перешли ли сюда с карты: флаг одноразовый, чтобы перезагрузка страницы не считалась переходом
const consumeCardVisit = (): boolean => {
    try {
        const from = sessionStorage.getItem(FROM_CARD_KEY)
        if (from === null) return false
        sessionStorage.removeItem(FROM_CARD_KEY)
        return from === window.location.pathname
    } catch {
        return false
    }
}

const LoadComponent = ({text}: {text: string}) => (
    <div className="get-qr-loader">
        <span className="loader-spinner" />
        <Text size="s" color="lightGray">{text}</Text>
    </div>
)

export const GetQrPage = () => {
    const [data, setData] = useState<StoreData | null>(null)
    const [error, setError] = useState(false)
    const [promoClosed, setPromoClosed] = useState(false)
    const [showSupport, setShowSupport] = useState(false)
    const isAdmin = !!localStorage.getItem('qr_admin_token')
    const promoRef = useRef<HTMLDivElement>(null)
    const [openedLinks, setOpenedLinks] = useState<number[]>([])
    const [rated, setRated] = useState(false)
    const [cardVisited, setCardVisited] = useState(false)
    const earnedPoints = openedLinks.length * POINTS_PER_LINK
        + (rated ? POINTS_PER_RATING : 0)
        + (cardVisited ? POINTS_PER_CARD_VISIT : 0)

    const handleLinkOpen = (linkId: number) => {
        setOpenedLinks((prev) => prev.includes(linkId) ? prev : [...prev, linkId])
    }

    const handleRated = () => setRated(true)

    useEffect(() => {
        if (consumeCardVisit()) setCardVisited(true)
    }, [])

    useEffect(() => {
        const storeId = getStoreId()

        fetch(`${API_URL}?store_id=${storeId}`)
            .then((res) => {
                if (!res.ok) throw new Error('request failed')
                return res.json()
            })
            .then((json: StoreData) => setData(json))
            .catch(() => setError(true))
    }, [])

    // useEffect(() => {
    //     if (data && data.links.length === 1) {
    //         trackLinkClick(data.links[0].id)
    //         window.location.href = data.links[0].link
    //     }
    // }, [data])

    useEffect(() => {
        if (!promoRef.current) return
        const tween = gsap.fromTo(promoRef.current,
            {xPercent: -50, x: 0, y: 40, opacity: 0},
            {y: 0, opacity: 1, duration: 0.6, ease: 'power3.out', delay: 1.2, clearProps: 'transform,opacity'},
        )
        return () => { tween.kill() }
    }, [data])

    if (error) {
        return (
            <PageComponent>
                <Text size="m" color="lightGray">Не нашли такую ссылку</Text>
            </PageComponent>
        )
    }

    if (!data) {
        return (
            <PageComponent>
                <LoadComponent text="Загрузка…" />
            </PageComponent>
        )
    }

    // if (data.links.length === 1) {
    //     return (
    //         <PageComponent>
    //             <LoadComponent text="Открываем ссылку…" />
    //         </PageComponent>
    //     )
    // }

    // Баллы начисляем только партнёрам и пока фича не скрыта флагом
    const showPoints = SHOW_POINT && data.isPartner

    return (
        <PageComponent center={false}>
            <div className="get-qr-scroll">
                {/* raiting в /get-links — это location магазина: есть координаты — есть оценки и точка на карте */}
                <StoreStyle
                    data={data}
                    onLinkOpen={showPoints ? handleLinkOpen : undefined}
                    openedLinkIds={showPoints ? openedLinks : undefined}
                    pointsPerLink={showPoints ? POINTS_PER_LINK : undefined}
                    rating={data.isPartner && data.raiting ? (
                        <>
                            <Rating
                                storeId={data.id}
                                onRated={showPoints ? handleRated : undefined}
                                rewardedPoints={showPoints && rated ? POINTS_PER_RATING : undefined}
                                action={
                                    <a className="get-qr-map-link" href={`/map/${data.id}`}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" />
                                            <circle cx="12" cy="9.5" r="2.5" />
                                        </svg>
                                        На карте
                                    </a>
                                }
                            />
                            {/* TODO: начисление баллов, пока заглушка */}
                            {showPoints && earnedPoints > 0 && (
                                <button type="button" className="get-qr-points-btn">
                                    Получить {earnedPoints} баллов
                                </button>
                            )}
                        </>
                    ) : undefined}
                />
                {/* <StoreStyle data={data} /> */}
            </div>
            <button type="button" className="get-qr-support-trigger" aria-label="Сообщить о проблеме" onClick={() => setShowSupport(true)}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 12.9V8.41447M12 16.2248V16.2642M17.6699 20H6.33007C4.7811 20 3.47392 18.9763 3.06265 17.5757C2.88709 16.9778 3.10281 16.3551 3.43276 15.8249L9.10269 5.60102C10.4311 3.46632 13.5689 3.46633 14.8973 5.60103L20.5672 15.8249C20.8972 16.3551 21.1129 16.9778 20.9373 17.5757C20.5261 18.9763 19.2189 20 17.6699 20Z" />
                </svg>
            </button>
            {!promoClosed && (
                <div ref={promoRef} className="get-qr-promo" onClick={() => { window.location.href = isAdmin ? '/admin' : '/' }}>
                    <Text size="xs" color="lightGray">{isAdmin ? 'Войти в Админ-панель' : 'Хотите такую же карту?'}</Text>
                    <button
                        type="button"
                        className="get-qr-promo-close"
                        aria-label="Закрыть"
                        onClick={(e) => { e.stopPropagation(); setPromoClosed(true) }}
                    >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                            <path d="M2 2l8 8M10 2l-8 8" />
                        </svg>
                    </button>
                </div>
            )}
            {showSupport && <SupportModal onClose={() => setShowSupport(false)} />}
        </PageComponent>
    )
}
